import {
  BasketballSwitchRequest,
  BasketballSwitchRequestSwitchType,
} from '@hefe/models';
import { formatDate, formatDateTime } from '@hefe/utils';
import { List, Popconfirm, Space, Spin, Typography } from 'antd';
import { t } from '@hefe/i18n';
import React, { useState } from 'react';
import { AiOutlineCheck, AiOutlineDelete } from 'react-icons/ai';
import { QuestionCircleOutlined } from '@ant-design/icons';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import { useAppDispatch } from '../../../../store/store';
import { BasketballSwitchRequestsSlice, ModalsSlice } from '@hefe/store';
import SwitchRequestsSwitchJudgesForm from '../../forms/switch-requests-switch-judges.form';
import SwitchRequestsSwitchSimpleForm from '../../forms/switch-requests-switch-simple.form';

interface SwitchRequestsItemComponentProps {
  request: BasketballSwitchRequest;
  userId: number;
}

export default function SwitchRequestsItemComponent(
  props: SwitchRequestsItemComponentProps
) {
  const { request, userId } = props;

  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);

  const onClickDelete = () => {
    setLoading(true);
    dispatch(
      BasketballSwitchRequestsSlice.deleteBasketballSwitchRequest(request)
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  };

  const onClickTakeOver = () => {
    let Component = null;

    switch (request.switch_type) {
      case BasketballSwitchRequestSwitchType.JUDGE:
        Component = <SwitchRequestsSwitchJudgesForm {...props} />;
        break;
      case BasketballSwitchRequestSwitchType.COACH:
        Component = <SwitchRequestsSwitchSimpleForm {...props} />;
        break;
      case BasketballSwitchRequestSwitchType.REFEREE:
        Component = <SwitchRequestsSwitchSimpleForm {...props} />;
        break;
      case BasketballSwitchRequestSwitchType.TASK:
        Component = <SwitchRequestsSwitchSimpleForm {...props} />;
        break;
      case BasketballSwitchRequestSwitchType.BASKETBALL_TEAM_TASK:
        Component = <SwitchRequestsSwitchSimpleForm {...props} />;
        break;
      default:
        return;
    }

    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: `switch-request-form-${request.id}`,
        title: t('switch_requests.singular'),
        component: Component,
      })
    );
  };

  const actions: any[] = [];
  if (isLoading) {
    actions.push(<Spin size="small" />);
  } else if (request.new_user_id == null) {
    if (request.user_id === userId) {
      actions.push(
        <Popconfirm
          title={t('questions.delete_request')}
          cancelText={t('cancel')}
          okText={t('delete')}
          okType="danger"
          onConfirm={() => onClickDelete()}
          placement="left"
          icon={<QuestionCircleOutlined />}
        >
          <AiOutlineDelete size={20} cursor="pointer" />
        </Popconfirm>
      );
    } else {
      actions.push(
        <AiOutlineCheck
          size={20}
          cursor="pointer"
          onClick={() => onClickTakeOver()}
        />
      );
    }
  }

  return (
    <List.Item actions={actions}>
      <List.Item.Meta
        avatar={<UserAvatar withTooltip user={request.user} />}
        title={
          <Space direction="vertical" size={0}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {request.title}
            </Typography.Title>
            <Typography.Text style={{ fontSize: 10 }}>
              {formatDateTime(request.event_date)} | {request.location}
            </Typography.Text>
          </Space>
        }
        description={
          request.new_user_id
            ? t('information_texts.take_over_by', {
                user: `${request.new_user?.firstname} ${request.new_user?.lastname}`,
              })
            : request.note
        }
      />
    </List.Item>
  );
}
